import { ROUTES } from '@/app/routes';
import { PAGE_TRANSITION, TAB_PAGE_TRANSITION, useReducedMotion } from '@/lib/motion-presets';
import { AnimatePresence, motion } from 'motion/react';
import { useLayoutEffect, type ReactNode } from 'react';

const TAB_PATHS = new Set<string>([ROUTES.profile, ROUTES.myClients, ROUTES.clients, ROUTES.builder]);

let previousShellPathname: string | null = null;

function isTabPath(pathname: string): boolean {
	return TAB_PATHS.has(pathname);
}

function isDetailPath(pathname: string): boolean {
	return (
		pathname.startsWith(ROUTES.myClient) ||
		pathname.startsWith(ROUTES.client) ||
		pathname === ROUTES.admin ||
		pathname === ROUTES.adminStats ||
		pathname === ROUTES.adminSettings ||
		pathname === ROUTES.adminUsers ||
		pathname.startsWith(ROUTES.adminUser)
	);
}

interface AnimatedPageProps {
	children: ReactNode;
	pathname: string;
	locationKey: string;
}

export default function AnimatedPage({ children, pathname, locationKey }: AnimatedPageProps) {
	const reducedMotion = useReducedMotion();
	const fromPathname = previousShellPathname ?? pathname;
	const isDetail = isDetailPath(pathname);
	const tabToTab = previousShellPathname !== null && isTabPath(fromPathname) && isTabPath(pathname);

	useLayoutEffect(() => {
		previousShellPathname = pathname;
	}, [pathname]);

	if (reducedMotion) {
		return (
			<div className='h-full w-full min-h-0 flex flex-col'>
				{children}
			</div>
		);
	}

	return (
		<AnimatePresence mode={tabToTab ? 'sync' : 'wait'} initial={false}>
			<motion.div
				key={locationKey}
				className='h-full w-full min-h-0 flex flex-col'
				initial={{ opacity: 0, x: tabToTab ? 0 : isDetail ? 8 : 0 }}
				animate={{ opacity: 1, x: 0 }}
				exit={{ opacity: 0, x: tabToTab ? 0 : isDetail ? -5 : 0 }}
				transition={tabToTab ? TAB_PAGE_TRANSITION : PAGE_TRANSITION}
			>
				{children}
			</motion.div>
		</AnimatePresence>
	);
}
